import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p9306Api, QueryParams, StaffingRow, DeptSummary, CompanyTotals } from '../../../services/p9306Api';

interface P9306QueryState {
  loading: boolean;
  data: StaffingRow[];
  summary: DeptSummary[];
  totals: CompanyTotals | null;
  queried: boolean;
  noData: boolean;
}

const initialTotals: CompanyTotals = {
  total_depts: 0,
  total_planned: 0,
  total_approved: 0,
  total_actual: 0,
};

export function useP9306Query() {
  const [state, setState] = useState<P9306QueryState>({
    loading: false,
    data: [],
    summary: [],
    totals: null,
    queried: false,
    noData: false,
  });

  const runQuery = useCallback(async (params: QueryParams) => {
    setState(prev => ({ ...prev, loading: true, queried: false, noData: false }));
    try {
      const result = await p9306Api.query(params);
      const hasData = result.data && result.data.length > 0;
      setState({
        loading: false,
        data: result.data || [],
        summary: result.summary || [],
        totals: result.totals || initialTotals,
        queried: true,
        noData: !hasData,
      });
      if (!hasData) {
        message.info(result.message || '查無符合條件的部門編制資料!');
      }
    } catch (err: unknown) {
      setState(prev => ({ ...prev, loading: false }));
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
        if (axiosErr.response?.status === 422) {
          message.warning(axiosErr.response.data?.error || '驗證錯誤');
          return;
        }
      }
      message.error('查詢失敗，請稍後再試');
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      loading: false,
      data: [],
      summary: [],
      totals: null,
      queried: false,
      noData: false,
    });
  }, []);

  const lookupDeptName = useCallback(async (code: string): Promise<string> => {
    return p9306Api.lookupName('department', code);
  }, []);

  return {
    loading: state.loading,
    data: state.data,
    summary: state.summary,
    totals: state.totals,
    queried: state.queried,
    noData: state.noData,
    runQuery,
    reset,
    lookupDeptName,
  };
}
